import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { GlobalContext } from "@/helpers/contexts/contexts";
import PageLayout from "@/components/layouts/page/PageLayout";
import DirectMailCampaignList from "../components/DirectMailCampaignList";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import useFetcher from "@/helpers/hooks/useFetcher";
import useInfiniteFetcher from "@/helpers/hooks/useInfiniteFetcher";
import useAutoLoadUntilScrollable from "@/helpers/hooks/useAutoLoadUntilScrollable";
import { CircularProgress, Typography } from "@mui/material";
import CampaignListFilters from "@/features/campaigns/shared/components/CampaignListFilters";

const PAGE_SIZE = 12;

type ParticipantData = {
  id?: string | number;
};

type LoggedInUser = {
  participantData?: ParticipantData;
};

type SelectedBrand = {
  id?: string | number;
};

type CampaignFilterOption = {
  id: string | number;
  [key: string]: any;
};

type DirectMailCampaignFilters = {
  tags: CampaignFilterOption[];
  search: string;
};

type DirectMailCampaignResponse = {
  postcardTabsData?: {
    data?: any[];
    pageCount?: number;
  };
};

const DirectMailCampaignListPage = () => {
  const { loggedInUser, selectedBrand } = useContext(GlobalContext) as {
    loggedInUser?: LoggedInUser;
    selectedBrand?: SelectedBrand;
  };
  const [filters, setFilters] = useState<DirectMailCampaignFilters>({
    tags: [],
    search: "",
  });

  const reqData = useMemo(() => {
    if (!selectedBrand?.id || !loggedInUser?.participantData?.id) return null;
    return {
      id: selectedBrand?.id,
      fk_participant_id: loggedInUser?.participantData?.id,
      userId: loggedInUser?.participantData?.id,
      offset: 0,
      limit: PAGE_SIZE,
      ...(filters.search && { campaign_name: filters.search.trim() }),
      ...(filters.tags.length > 0 && {
        tag_id: filters.tags.map((t) => t.id).join(","),
      }),
    };
  }, [
    selectedBrand?.id,
    loggedInUser?.participantData?.id,
    filters.search,
    filters.tags,
  ]);

  const { data, isLoading, isValidating, size, setSize } =
    useInfiniteFetcher<DirectMailCampaignResponse>(
      reqData,
      campaignService.launchPostcardCampaigns,
      { revalidateOnFocus: false, parallel: true }
    );

  const handleFilterChange = useCallback(
    (filterType: keyof DirectMailCampaignFilters, value: any) => {
      setFilters((prev) => ({ ...prev, [filterType]: value }));
    },
    []
  );

  const handleSearchChange = useCallback(
    (value: string) => handleFilterChange("search", value),
    [handleFilterChange]
  );

  const campaigns = data?.flatMap((d) => d?.postcardTabsData?.data || []) ?? [];

  const { data: tagsData } = useFetcher(
    loggedInUser?.participantData?.id && selectedBrand?.id
      ? {
          fk_participant_id: loggedInUser.participantData.id,
          id: selectedBrand.id,
        }
      : null,
    campaignService.getPostCardCampaignTagsParticipant,
    { revalidateOnFocus: false }
  );

  const tagsList = tagsData?.tagsList || [];
  const isLastPage =
    size >= data?.[data?.length - 1]?.postcardTabsData?.pageCount;
  const hasMore = !isLastPage;

  useAutoLoadUntilScrollable({
    containerId: "launch-postcard",
    isLastPage,
    dependency: campaigns.length,
    triggerLoad: () => {
      if (!isValidating && data?.length > 0) setSize((prev) => prev + 1);
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrolledTo = window.scrollY + window.innerHeight;
      const isNearBottom = document.body.scrollHeight - 400 <= scrolledTo;
      if (isNearBottom && !isLastPage && !isValidating) {
        setSize((prev) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLastPage, isValidating, setSize]);

  return (
    <PageLayout className="participant-dashboard-container mx-0 mx-sm-2">
      <div className="dashboard participant-dashboard" id="launch-postcard">
        <div className="row justify-content-end align-items-center my-3">
          <div className="col">
            <Typography variant="h5" className="widget-title mb-4 mb-md-auto">
              Launch Direct Mail
            </Typography>
          </div>
        </div>
        <CampaignListFilters
          isLoading={isLoading}
          onFilterChange={handleFilterChange}
          tagsList={tagsList}
          channelsList={[]}
          filters={filters}
          searchValue={filters.search}
          onSearchChange={handleSearchChange}
        />
        <DirectMailCampaignList
          campaigns={campaigns}
          isLoading={isLoading}
          searchValue={filters.search}
        />
        {!isLoading && isValidating && hasMore && (
          <div className="text-center bg-light">
            <Typography variant="caption" color="text.secondary">
              <CircularProgress color="primary" size={10} className="mr-1" />{" "}
              Loading ...
            </Typography>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default DirectMailCampaignListPage;
